import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, TrendingDown, Ticket, Clock, Download, Percent, Wallet, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { money } from './finances/financeUtils';
import { SummaryCard, MiniCard } from './finances/FinancesCards';
import FinancesChart from './finances/FinancesChart';
import FinancesByEventTable from './finances/FinancesByEventTable';
import FinancesTransactions from './finances/FinancesTransactions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrganizerFinances = () => {
  const [summary, setSummary] = useState(null);
  const [byEvent, setByEvent] = useState(null);
  const [txns, setTxns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_id: '', status: '' });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, t] = await Promise.all([
        axios.get(`${API}/organizer/finances/summary`, { withCredentials: true }),
        axios.get(`${API}/organizer/finances/by-event`, { withCredentials: true }),
        axios.get(`${API}/organizer/finances/transactions?limit=25`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setByEvent(e.data);
      setTxns(t.data);
    } catch (err) {
      toast.error('No pudimos cargar las finanzas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.event_id) params.set('event_id', filters.event_id);
      if (filters.status) params.set('status', filters.status);
      params.set('limit', '25');
      const t = await axios.get(`${API}/organizer/finances/transactions?${params}`, {
        withCredentials: true,
      });
      setTxns(t.data);
    } catch {
      toast.error('Error al filtrar');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.event_id) params.set('event_id', filters.event_id);
      if (filters.status) params.set('status', filters.status);
      const resp = await axios.get(`${API}/organizer/finances/export?${params}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="(.+)"/);
      link.download = match ? match[1] : 'mitribu-finanzas.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch {
      toast.error('Error al descargar CSV');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="space-y-6" data-testid="finances-loading">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white/60 border border-[#E5E5E5] rounded-lg h-32 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const hasSales = summary.sold_tickets > 0;
  const chartData = (byEvent?.events || []).filter(e => e.gross > 0).slice(0, 8).map(e => ({
    name: e.title.length > 18 ? e.title.slice(0, 16) + '…' : e.title,
    gross: e.gross,
    net: e.net,
  }));

  return (
    <div className="space-y-6" data-testid="organizer-finances">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#0A0A0A]">Finanzas</h2>
          <p className="text-sm text-[#737373]">
            Comisión Mi Tribu: <strong className="text-[#0A0A0A]">{summary.commission_pct}%</strong> sobre entradas aprobadas ·
            Moneda: <strong className="text-[#0A0A0A]">{summary.currency}</strong>
          </p>
        </div>
        <Button
          onClick={exportCSV}
          disabled={exporting || !hasSales}
          className="bg-[#0A0A0A] hover:bg-[#171717] text-white font-medium"
          data-testid="finances-export-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Descargando...' : 'Descargar CSV'}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total facturado (bruto)"
          value={money(summary.gross, summary.currency)}
          icon={<DollarSign className="w-5 h-5" />}
          testid="fin-card-gross"
          accent="text-[#0A0A0A]"
        />
        <SummaryCard
          title={`Comisión (${summary.commission_pct}%)`}
          value={money(summary.commission, summary.currency)}
          icon={<Percent className="w-5 h-5" />}
          testid="fin-card-commission"
          accent="text-red-600"
        />
        <SummaryCard
          title="Ingreso neto"
          value={money(summary.net, summary.currency)}
          icon={<Wallet className="w-5 h-5" />}
          testid="fin-card-net"
          accent="text-green-700"
          highlight
        />
        <SummaryCard
          title="Entradas vendidas"
          value={summary.sold_tickets.toLocaleString('es-AR')}
          subtitle={summary.average_ticket ? `Ticket promedio: ${money(summary.average_ticket)}` : null}
          icon={<Ticket className="w-5 h-5" />}
          testid="fin-card-tickets"
          accent="text-[#0A0A0A]"
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MiniCard
          icon={<Clock className="w-4 h-4 text-yellow-600" />}
          label="Pipeline pendiente"
          value={money(summary.pending_amount, summary.currency)}
          hint={`${summary.pending_count} pago(s) esperando confirmación`}
          testid="fin-card-pending"
        />
        <MiniCard
          icon={<TrendingDown className="w-4 h-4 text-red-500" />}
          label="Pagos rechazados"
          value={summary.failed_count.toString()}
          hint="Capacidad liberada automáticamente"
          testid="fin-card-failed"
        />
        <MiniCard
          icon={<TrendingUp className="w-4 h-4 text-green-600" />}
          label="Eventos con ventas"
          value={summary.events_with_sales.toString()}
          hint={`De ${byEvent?.events?.length || 0} eventos totales`}
          testid="fin-card-events-with-sales"
        />
      </div>

      {!hasSales ? (
        <Card className="bg-white/60 border-[#E5E5E5]" data-testid="finances-empty">
          <CardContent className="py-16 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-[#F5F5F5] flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-[#737373]" />
            </div>
            <h3 className="text-lg font-semibold text-[#0A0A0A]">Todavía no hay ventas confirmadas</h3>
            <p className="text-sm text-[#737373] max-w-md mx-auto">
              Cuando vendas tu primera entrada por MercadoPago vas a ver acá el desglose por evento,
              tus ingresos netos y podrás exportar todo a CSV para tu contador.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <FinancesChart chartData={chartData} />
          <FinancesByEventTable events={byEvent?.events || []} />
          <FinancesTransactions
            txns={txns}
            events={byEvent?.events || []}
            filters={filters}
            setFilters={setFilters}
            onApply={applyFilters}
          />
        </>
      )}
    </div>
  );
};

export default OrganizerFinances;
